import { createContext, useContext, useEffect, useState } from "react";
import {
  EmailAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase/firebase";
import { writeAuditLog, writeUserProfile } from "../firebase/audit";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState("viewer");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        try {
          const profileRef = doc(db, "users", currentUser.uid);
          await writeUserProfile(currentUser);

          let profileSnapshot = await getDoc(profileRef);
          if (!profileSnapshot.exists() || !profileSnapshot.data()?.role) {
            await setDoc(profileRef, { role: "viewer" }, { merge: true });
            profileSnapshot = await getDoc(profileRef);
          }

          const profileRole = String(profileSnapshot.data()?.role || "viewer").toLowerCase();
          setRole(["admin", "reviewer", "viewer"].includes(profileRole) ? profileRole : "viewer");
        } catch {
          setRole("viewer");
        }
      } else {
        setRole("viewer");
      }

      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email.trim(), password);
    writeAuditLog({
      action: "SIGN_IN",
      entityType: "user",
      entityId: result.user.uid,
      user: result.user,
    }).catch(() => {});
    return result;
  };

  const signOutUser = () => signOut(auth);

  const changePassword = async (currentPassword, newPassword) => {
    if (!user?.email) {
      throw new Error("No signed-in user.");
    }

    const credential = EmailAuthProvider.credential(
      user.email,
      currentPassword
    );
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  };

  return (
    <AuthContext.Provider value={{
      user,
      role,
      isLoading,
      signIn,
      signOut: signOutUser,
      changePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return context;
}
